import { useEffect, useState } from 'react';
import { Box, TextField, Alert, AlertTitle, Typography, Autocomplete } from '@mui/material';
import { extractXmlFieldPaths, type FieldPath } from './utils/parseXml';

interface XmlRedirectFieldsProps {
  onSampleChange: (value: string) => void;
  onRedirectFieldChange: (value: string) => void;
  size?: 'small' | 'medium';
  placeholder?: string;
  values: {
    xmlSample: string;
    xmlRedirectField: string;
  };
  errors?: {
    xmlSample: string;
    xmlRedirectField: string;
  };
}

export function XmlRedirectFields({
  onSampleChange,
  onRedirectFieldChange,
  size = 'small',
  placeholder,
  values,
  errors = { xmlSample: '', xmlRedirectField: '' },
}: XmlRedirectFieldsProps) {
  const [xmlFieldPaths, setXmlFieldPaths] = useState<FieldPath[]>([]);
  const [xmlParseError, setXmlParseError] = useState<string>('');

  const [innerXmlSample, setInnerXmlSample] = useState(values.xmlSample);
  const [innerXmlRedirectField, setInnerXmlRedirectField] = useState(values.xmlRedirectField);

  useEffect(() => {
    const id = setTimeout(() => {
      onSampleChange(innerXmlSample);
    }, 500);

    return () => clearTimeout(id);
  }, [innerXmlSample]);

  useEffect(() => {
    const id = setTimeout(() => {
      onRedirectFieldChange(innerXmlRedirectField);
    }, 500);

    return () => clearTimeout(id);
  }, [innerXmlRedirectField]);

  useEffect(() => {
    const xmlSample = values.xmlSample;

    const hasXmlSample = xmlSample && xmlSample.trim().length > 0;

    if (!hasXmlSample) {
      setXmlParseError('');
      return;
    }

    try {
      const paths = extractXmlFieldPaths(xmlSample);
      setXmlFieldPaths(paths);
      setXmlParseError('');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse XML';
      setXmlParseError(errorMessage);
      setXmlFieldPaths([]);
    }
  }, [values.xmlSample]);

  const fieldSize = size === 'small' ? 'small' : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: size === 'small' ? 1 : 2 }}>
      <TextField
        size={fieldSize}
        fullWidth
        multiline
        rows={size === 'small' ? 4 : 6}
        label="XML Sample"
        name="xmlSample"
        value={innerXmlSample}
        onChange={(e) => setInnerXmlSample(e.target.value)}
        error={!!xmlParseError}
        helperText={xmlParseError || 'Paste a sample XML to extract available fields (optional)'}
        placeholder={placeholder}
        margin="dense"
      />

      <Autocomplete
        size={fieldSize}
        options={xmlFieldPaths}
        value={innerXmlRedirectField}
        freeSolo
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            return setInnerXmlRedirectField(newValue);
          } 
          if (newValue && 'path' in newValue) {
            return setInnerXmlRedirectField(newValue.path);
          }
          return setInnerXmlRedirectField('');
          }
        }

        onInputChange={(event, newInputValue) => {
          return setInnerXmlRedirectField(newInputValue);
        }}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return option.path;
        }}
        isOptionEqualToValue={(option, value) => {
          return option.path === value.path;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            name="xmlRedirectField"
            label="XML Redirect Field"
            margin="dense"
            error={!!errors.xmlRedirectField}
            helperText={errors.xmlRedirectField || 'Select which XML field to extract the redirect URL'}
          />
        )}
        fullWidth
      />

      <Alert severity="info" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
        <AlertTitle>Info</AlertTitle>
        <Typography>If the system detects fields with the same name, it will use the first one.</Typography>
        <Box width="100%" bgcolor="#f0f0f0" p={2} borderRadius={1} my={2}>
          {`<result>`}
          <br />
          &emsp;
          {`<listing title="Nike" descr="first offer" site="site.com" bid="0.5"/>`}
          <br />
          &emsp;
          {`<listing title="Adidas" descr="second offer" site="site2.com" bid="0.6"/>`}
          <br />
          {`</result>`}
        </Box>
        <Typography>For example, if you select "result.listing.title", the system will use "Nike" as the redirect URL since it is the first one.</Typography>
      </Alert>
    </Box>
  );
}
